import React, { createContext, useContext, useState, useEffect, useMemo, useRef } from 'react';
import { Platform, View, Text } from 'react-native';
import { useAuth } from './AuthContext';

// Firestore project ID for direct REST API calls
const FIREBASE_PROJECT_ID = 'twelve-month-millionaire';

const NavigationContext = createContext();

// Screen to URL mapping
const screenToUrl = {
  'Landing': '/',
  'Login': '/login',
  'GettingStarted': '/getting-started',
  'ForgotPassword': '/forgot-password',
  'MyDashboard': '/dashboard',
  'Courses': '/courses',
  'Glossary': '/glossary',
  'Calendar': '/calendar',
  'Profile': '/profile',
  'Settings': '/settings',
  'Admin': '/admin',
  'ComingSoon': '/coming-soon',
  'Maintenance': '/maintenance',
  'Suspended': '/suspended',
  'TigerTeam': '/team',
};

// URL to screen mapping
const urlToScreen = {
  '/': 'Landing',
  '/landing': 'Landing',
  '/login': 'Login',
  '/getting-started': 'GettingStarted',
  '/forgot-password': 'ForgotPassword',
  '/home': 'MyDashboard',
  '/dashboard': 'MyDashboard',
  '/courses': 'Courses',
  '/glossary': 'Glossary',
  '/calendar': 'Calendar',
  '/profile': 'Profile',
  '/settings': 'Settings',
  '/admin': 'Admin',
  '/coming-soon': 'ComingSoon',
  '/maintenance': 'Maintenance',
  '/suspended': 'Suspended',
  '/team': 'TigerTeam',
};

// Helper to get route from location
const getRouteFromLocation = () => {
  if (Platform.OS !== 'web') return '/';
  try {
    const path = window?.location?.pathname || '/';
    const hash = window?.location?.hash || '';
    if (hash.startsWith('#/')) return hash.slice(1);
    return path;
  } catch {
    return '/';
  }
};

export function NavigationProvider({ children }) {
  const { user: authUser, loading: authLoading } = useAuth();
  const [currentScreen, setCurrentScreen] = useState('Landing');
  const [history, setHistory] = useState(['Landing']);
  const [redirectAfterLogin, setRedirectAfterLogin] = useState(null);
  const [routePayload, setRoutePayload] = useState(null);
  const [initializing, setInitializing] = useState(true);
  const lastUidRef = useRef(null);
  const didNavigateRef = useRef(false);

  // Get userType from AuthContext's user object
  // userType is now managed in AuthContext with real-time updates
  const userType = authUser?.userType || null;
  const isAdmin = userType === 'admin';

  // Initialize flag - set to false once auth is loaded
  useEffect(() => {
    if (!authLoading) {
      setInitializing(false);
    }
  }, [authLoading]);

  // Handle navigation after login/registration
  // This listens to authUser changes and navigates accordingly
  useEffect(() => {
    if (authLoading || initializing) return;
    
    if (authUser) {
      // User is logged in
      const isNewUser = lastUidRef.current !== authUser.uid;
      
      // Reset navigation flag if it's a new user
      if (isNewUser) {
        didNavigateRef.current = false;
        lastUidRef.current = authUser.uid;
      }
      
      // Check if user is suspended
      if (authUser.suspended) {
        navigateTo('Suspended');
        didNavigateRef.current = true;
        return;
      }
      
      // Auto-navigate on fresh login/registration (not on page refresh)
      if (!didNavigateRef.current) {
        let shouldAutoNavigate = false;
        
        if (Platform.OS === 'web') {
          try {
            const path = window?.location?.pathname || '/';
            // Protected paths - user refreshed page, don't redirect
            const protectedPaths = ['/dashboard', '/courses', '/glossary', '/calendar', '/profile', '/settings', '/admin', '/suspended'];
            // Public/auth paths - fresh login, should redirect
            const publicPaths = ['/', '/landing', '/login', '/getting-started'];
            
            if (publicPaths.includes(path)) {
              shouldAutoNavigate = true;
            } else if (!protectedPaths.includes(path)) {
              // Unknown path, allow auto-navigate as fallback
              shouldAutoNavigate = true;
            }
          } catch (e) {
            // If can't check URL, don't auto-navigate (safe fallback)
            shouldAutoNavigate = false;
          }
        } else {
          // On native, check currentScreen
          shouldAutoNavigate = (currentScreen === 'Landing' || currentScreen === 'Login' || currentScreen === 'GettingStarted');
        }
        
        if (shouldAutoNavigate) {
          navigateTo('MyDashboard');
          didNavigateRef.current = true;
        }
      }
    } else {
      // User logged out
      lastUidRef.current = null;
      didNavigateRef.current = false;
      
      // If on a protected screen, redirect to landing
      const protectedScreens = ['Home', 'Courses', 'Glossary', 'Calendar', 'Profile', 'Admin', 'MyDashboard', 'Settings', 'Suspended'];
      if (protectedScreens.includes(currentScreen)) {
        navigateTo('Landing');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser, authLoading, initializing, currentScreen]);
  
  // Maintenance state - Legacy global settings
  const [comingSoonEnabled, setComingSoonEnabled] = useState(false);
  const [bannerEnabled, setBannerEnabled] = useState(false);
  const [bannerStartedAt, setBannerStartedAt] = useState(null);
  
  // New independent web/mobile maintenance states
  const [webComingSoonEnabled, setWebComingSoonEnabled] = useState(false);
  const [webMaintenanceEnabled, setWebMaintenanceEnabled] = useState(false);
  const [mobileComingSoonEnabled, setMobileComingSoonEnabled] = useState(false);
  const [mobileMaintenanceEnabled, setMobileMaintenanceEnabled] = useState(false);
  
  // Maintenance settings loader - uses direct REST API to bypass Firestore SDK issues
  useEffect(() => {
    let isMounted = true;
    let pollInterval = null;

    const loadMaintenanceSettings = async () => {
      try {
        // Use direct Firestore REST API to completely bypass SDK internal state issues
        const url = `https://firestore.googleapis.com/v1/projects/${FIREBASE_PROJECT_ID}/databases/(default)/documents/site_maintenance/global`;
        
        const response = await fetch(url);
        
        if (!isMounted) return;
        
        if (response.ok) {
          const result = await response.json();
          
          // Parse Firestore REST API response format
          const fields = result.fields || {};
          const data = {
            comingSoonEnabled: fields.comingSoonEnabled?.booleanValue || false,
            maintenanceBannerEnabled: fields.maintenanceBannerEnabled?.booleanValue || false,
            maintenanceBannerStartedAt: fields.maintenanceBannerStartedAt?.timestampValue || null,
            webComingSoonEnabled: fields.webComingSoonEnabled?.booleanValue || false,
            webMaintenanceEnabled: fields.webMaintenanceEnabled?.booleanValue || false,
            mobileComingSoonEnabled: fields.mobileComingSoonEnabled?.booleanValue || false,
            mobileMaintenanceEnabled: fields.mobileMaintenanceEnabled?.booleanValue || false,
          };
          
          console.log('[NavigationContext] Maintenance data loaded via REST:', data);
          
          // Legacy global settings
          setComingSoonEnabled(data.comingSoonEnabled);
          setBannerEnabled(data.maintenanceBannerEnabled);
          setBannerStartedAt(data.maintenanceBannerStartedAt);
          // New independent settings
          setWebComingSoonEnabled(data.webComingSoonEnabled);
          setWebMaintenanceEnabled(data.webMaintenanceEnabled);
          setMobileComingSoonEnabled(data.mobileComingSoonEnabled);
          setMobileMaintenanceEnabled(data.mobileMaintenanceEnabled);
          
          console.log('[NavigationContext] State updated - webComingSoon:', data.webComingSoonEnabled, 'webMaintenance:', data.webMaintenanceEnabled);
        } else if (response.status === 404) {
          console.log('[NavigationContext] No maintenance document found');
        } else {
          console.error('[NavigationContext] REST API error:', response.status, response.statusText);
        }
      } catch (err) {
        console.error('[NavigationContext] Error loading maintenance settings:', err);
      }
    };

    // Initial load
    console.log('[NavigationContext] Loading maintenance settings via REST API...');
    loadMaintenanceSettings();
    
    // Poll every 30 seconds to catch updates (maintenance settings don't need real-time)
    pollInterval = setInterval(loadMaintenanceSettings, 30000);

    return () => {
      isMounted = false;
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, []);

  // Prod host detection (bypass coming soon in development)
  const isProdHost = useMemo(() => {
    if (Platform.OS !== 'web') return false;
    try {
      const host = window.location.hostname;
      return host && host !== 'localhost' && host !== '127.0.0.1';
    } catch {
      return false;
    }
  }, []);

  // Determine if current platform should show maintenance/coming soon
  const shouldShowMaintenancePage = useMemo(() => {
    const result = (() => {
      if (isAdmin) return false;
      
      if (Platform.OS === 'web') {
        // Check web-specific settings first, then fall back to global
        return webMaintenanceEnabled || webComingSoonEnabled || comingSoonEnabled;
      } else {
        // Mobile platform
        return mobileMaintenanceEnabled || mobileComingSoonEnabled || comingSoonEnabled;
      }
    })();
    console.log('[NavigationContext] shouldShowMaintenancePage computed:', result, '| isAdmin:', isAdmin, '| webComingSoon:', webComingSoonEnabled, '| webMaintenance:', webMaintenanceEnabled);
    return result;
  }, [isAdmin, webMaintenanceEnabled, webComingSoonEnabled, mobileMaintenanceEnabled, mobileComingSoonEnabled, comingSoonEnabled]);

  // Get the type of maintenance page to show
  const maintenancePageType = useMemo(() => {
    let result = null;
    if (Platform.OS === 'web') {
      if (webMaintenanceEnabled) result = 'maintenance';
      else if (webComingSoonEnabled) result = 'comingSoon';
    } else {
      if (mobileMaintenanceEnabled) result = 'maintenance';
      else if (mobileComingSoonEnabled) result = 'comingSoon';
    }
    // Fallback to legacy
    if (!result && comingSoonEnabled) result = 'comingSoon';
    console.log('[NavigationContext] maintenancePageType computed:', result);
    return result;
  }, [webMaintenanceEnabled, webComingSoonEnabled, mobileMaintenanceEnabled, mobileComingSoonEnabled, comingSoonEnabled]);

  // Redirect users without access to coming-soon/maintenance when enabled
  // Works in both development and production, but admins can bypass
  useEffect(() => {
    console.log('[NavigationContext] Redirect effect check - shouldShow:', shouldShowMaintenancePage, '| pageType:', maintenancePageType, '| currentScreen:', currentScreen);
    if (shouldShowMaintenancePage && currentScreen !== 'ComingSoon' && currentScreen !== 'Maintenance' && currentScreen !== 'Admin') {
      console.log('[NavigationContext] Redirecting to maintenance/coming soon page...');
      try {
        const targetScreen = maintenancePageType === 'maintenance' ? 'Maintenance' : 'ComingSoon';
        const targetPath = maintenancePageType === 'maintenance' ? '/maintenance' : '/coming-soon';
        
        console.log('[NavigationContext] Target:', targetScreen, targetPath);
        if (Platform.OS === 'web') {
          window.history.pushState({}, '', targetPath);
          window.dispatchEvent(new Event('app:navigate'));
        } else {
          setCurrentScreen(targetScreen);
        }
      } catch (err) {
        console.error('[NavigationContext] Redirect error:', err);
        setCurrentScreen(maintenancePageType === 'maintenance' ? 'Maintenance' : 'ComingSoon');
      }
    }
  }, [shouldShowMaintenancePage, maintenancePageType, currentScreen]);

  // Initialize screen from URL on web
  useEffect(() => {
    if (Platform.OS === 'web' && !initializing) {
      const path = getRouteFromLocation();
      const screen = urlToScreen[path] || 'Landing';
      
      // If not logged in and trying to access protected screens, redirect to Landing
      if (!authUser && (screen === 'Home' || screen === 'Courses' || screen === 'Glossary' || screen === 'Calendar' || screen === 'Profile' || screen === 'Admin')) {
        setCurrentScreen('Landing');
        setHistory(['Landing']);
        window.history.replaceState({}, '', '/');
      } else {
        setCurrentScreen(screen);
        setHistory([screen]);
      }
    }
  }, [authUser, initializing]);

  // Set document title based on current screen
  useEffect(() => {
    if (Platform.OS === 'web') {
      const screenTitles = {
        'Landing': 'Home | High Risk World',
        'Login': 'Sign In | High Risk World',
        'ForgotPassword': 'Forgot Password | High Risk World',
        'GettingStarted': 'Get Started | High Risk World',
        'MyDashboard': 'Dashboard | High Risk World',
        'Courses': 'Courses | High Risk World',
        'Profile': 'Profile | High Risk World',
        'Settings': 'Settings | High Risk World',
        'Admin': 'Admin | High Risk World',
        'ComingSoon': 'Coming Soon | High Risk World',
        'Maintenance': 'Under Maintenance | High Risk World',
        'Suspended': 'Account Suspended | High Risk World',
      };
      
      const title = screenTitles[currentScreen] || 'High Risk World';
      document.title = title;
    }
  }, [currentScreen]);

  // Listen for browser navigation events (back/forward buttons and custom events)
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    
    const handlePopState = () => {
      const path = getRouteFromLocation();
      const screen = urlToScreen[path] || 'Landing';
      
      // Check if user should have access
      const protectedScreens = ['Home', 'Courses', 'Glossary', 'Calendar', 'Profile', 'Admin', 'MyDashboard', 'Settings'];
      if (!authUser && protectedScreens.includes(screen)) {
        setCurrentScreen('Landing');
        window.history.replaceState({}, '', '/');
      } else if (screen === 'Admin' && !isAdmin) {
        setCurrentScreen('Landing');
        window.history.replaceState({}, '', '/');
      } else {
        setCurrentScreen(screen);
        setRoutePayload(null);
        
        // Track course engagement for daily decision auto-popup management
        // Note: This will be handled by each screen individually to avoid circular dependencies
      }
    };

    const handleNavigate = () => {
      const path = getRouteFromLocation();
      const screen = urlToScreen[path] || 'Landing';
      const protectedScreens = ['Home', 'Courses', 'Glossary', 'Calendar', 'Profile', 'Admin', 'MyDashboard', 'Settings', 'Suspended'];
      
      if (!authUser && protectedScreens.includes(screen)) {
        setCurrentScreen('Landing');
        window.history.replaceState({}, '', '/');
      } else if (screen === 'Admin' && !isAdmin) {
        setCurrentScreen('Landing');
        window.history.replaceState({}, '', '/');
      } else {
        setCurrentScreen(screen);
      }
    };

    window.addEventListener('popstate', handlePopState);
    window.addEventListener('app:navigate', handleNavigate);
    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('app:navigate', handleNavigate);
    };
  }, [authUser, isAdmin]);

  const navigateTo = (screenName, options = {}) => {
    // Don't navigate if already on the same screen
    if (currentScreen === screenName) {
      return;
    }

    // Handle redirect after login
    if (screenName === 'Login' && options.redirectAfterLogin) {
      setRedirectAfterLogin(options.redirectAfterLogin);
    }

    // Check auth requirements
    const protectedScreens = ['Home', 'Courses', 'Glossary', 'Calendar', 'Profile', 'Admin', 'MyDashboard', 'Settings'];
    if (protectedScreens.includes(screenName) && !authUser) {
      // Redirect to login instead, saving the intended destination
      navigateTo('Login', { redirectAfterLogin: screenName });
      return;
    }

    // Admin only screens
    if (screenName === 'Admin' && !isAdmin) {
      return;
    }

    setCurrentScreen(screenName);
    setRoutePayload(options?.payload || null);
    setHistory(prev => [...prev, screenName]);

    // Update URL on web
    if (Platform.OS === 'web') {
      const url = screenToUrl[screenName] || '/';
      window.history.pushState({}, '', url);
      // Dispatch custom event for any listeners
      try {
        window.dispatchEvent(new Event('app:navigate'));
      } catch (e) {
        // Fallback for older browsers
      }
    }
  };

  const goBack = () => {
    if (history.length > 1) {
      const newHistory = [...history];
      newHistory.pop();
      const previousScreen = newHistory[newHistory.length - 1];
      
      setCurrentScreen(previousScreen);
      setRoutePayload(null);
      setHistory(newHistory);

      // Update URL on web
      if (Platform.OS === 'web') {
        const url = screenToUrl[previousScreen] || '/';
        window.history.pushState({}, '', url);
        try {
          window.dispatchEvent(new Event('app:navigate'));
        } catch (e) {}
      }
    }
  };

  const replaceScreen = (screenName, options = {}) => {
    setCurrentScreen(screenName);
    setRoutePayload(options?.payload || null);
    setHistory(prev => [...prev.slice(0, -1), screenName]);

    // Update URL on web
    if (Platform.OS === 'web') {
      const url = screenToUrl[screenName] || '/';
      window.history.replaceState({}, '', url);
      try {
        window.dispatchEvent(new Event('app:navigate'));
      } catch (e) {}
    }
  };

  const handleLoginRedirect = () => {
    if (redirectAfterLogin) {
      const redirectScreen = redirectAfterLogin;
      setRedirectAfterLogin(null);
      navigateTo(redirectScreen);
    } else {
      navigateTo('MyDashboard');
    }
  };

  // Banner component (returns JSX, not a component function)
  const getBannerDuration = () => {
    if (!bannerStartedAt) return '';
    try {
      const startTime = bannerStartedAt.toDate ? bannerStartedAt.toDate() : new Date(bannerStartedAt);
      const now = new Date();
      const diffMs = now - startTime;
      const hours = Math.floor(diffMs / (1000 * 60 * 60));
      const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);
      return `${hours}h ${minutes}m ${seconds}s`;
    } catch {
      return '';
    }
  };

  const Banner = () => {
    const [duration, setDuration] = useState(getBannerDuration());

    useEffect(() => {
      if (!bannerEnabled || !bannerStartedAt) return;
      const interval = setInterval(() => {
        setDuration(getBannerDuration());
      }, 1000);
      return () => clearInterval(interval);
    }, [bannerEnabled, bannerStartedAt]);

    if (!bannerEnabled) return null;

    return (
      <View style={bannerStyles.banner}>
        <Text style={bannerStyles.bannerText}>
          Site maintenance in progress{duration ? ` — ${duration}` : ''}
          {bannerStartedAt && (
            <Text style={bannerStyles.bannerSubtext}>
              {` (started ${bannerStartedAt.toDate ? bannerStartedAt.toDate().toLocaleString() : new Date(bannerStartedAt).toLocaleString()})`}
            </Text>
          )}
        </Text>
      </View>
    );
  };

  return (
    <NavigationContext.Provider value={{
      currentScreen,
      navigateTo,
      goBack,
      replaceScreen,
      history,
      handleLoginRedirect,
      redirectAfterLogin,
      routePayload,
      authUser: authUser || null,
      userType,
      isAdmin,
      initializing,
      // Legacy global settings
      comingSoonEnabled,
      bannerEnabled,
      bannerStartedAt,
      // New independent web/mobile settings
      webComingSoonEnabled,
      webMaintenanceEnabled,
      mobileComingSoonEnabled,
      mobileMaintenanceEnabled,
      // Computed maintenance state
      shouldShowMaintenancePage,
      maintenancePageType,
      Banner,
    }}>
      {children}
    </NavigationContext.Provider>
  );
}

export function useNavigation() {
  const context = useContext(NavigationContext);
  if (context === undefined) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
}

// Banner styles
const bannerStyles = {
  banner: {
    backgroundColor: 'rgba(234, 128, 107, 0.95)',
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(234, 128, 107, 0.40)',
    paddingVertical: 10,
    paddingHorizontal: 16,
    alignItems: 'center',
    justifyContent: 'center',
    ...Platform.select({
      web: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
      },
    }),
  },
  bannerText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontFamily: 'Archivo',
    fontWeight: '600',
    textAlign: 'center',
    lineHeight: 20,
  },
  bannerSubtext: {
    fontSize: 12,
    opacity: 0.9,
    fontFamily: 'Archivo',
    color: '#FFFFFF',
  },
};

